/* eslint-disable max-lines */
import { useEffect, useMemo, useReducer } from 'react';

import { useCalls } from '@usedapp/core';
import { BigNumber, utils } from 'ethers';

import {
  DEFAULT_BIGNUM,
  DEFAULT_DECIMALS,
  SECONDS_IN_YEAR,
  TEST_ACCOUNT,
} from '@context/constants';
import { useDexRates } from '@context/providers/DexRatesProvider';
import { useExchangeRates } from '@context/providers/ExchangeRatesProvider';
import { useLiquidityPairs } from '@context/providers/LiquidityPairsProvider';
import { useTrackedState } from '@context/store';

import { ZERO_ADDRESS } from '@views/Swap/context/constants';

import {
  chunkArray,
  findProvider,
  getStakingContract,
  lpTokenValue,
} from '@helpers/index';

import { getFarmListForProvider } from '@views/Farms/helpers/getFarmListForProvider';
import { isFarmRewardsPeriodEnded } from '@views/Farms/helpers/isFarmRewardsPeriodEnded';
import { sortFarmsList } from '@views/Farms/helpers/sortFarmsList';
import { getStakingAPY } from '@views/Staking/helpers/getStakingAPY';

import { useAppWallet } from '@hooks/useAppWallet';

type FarmContextState = {
  farmPairs: IFarmPair[];
  farmBalances: Record<string, BigNumber>;
  farmTotalSupply: Record<string, BigNumber>;
  farmEarned: Record<string, BigNumber>;
};

const DEFAULT_STATE: FarmContextState = {
  farmPairs: [],
  farmBalances: {},
  farmEarned: {},
  farmTotalSupply: {},
};

type SetFarmPairsAction = { type: 'SET_FARMS'; payload: IFarmPair[] };
type SetFarmBalancesAction = {
  type: 'SET_BALANCES';
  payload: Record<string, BigNumber>;
};
type SetFarmTotalSupplyAction = {
  type: 'SET_TOTAL_SUPPLY';
  payload: Record<string, BigNumber>;
};
type SetFarmEarnedAction = {
  type: 'SET_EARNED';
  payload: Record<string, BigNumber>;
};

type Action =
  | SetFarmPairsAction
  | SetFarmBalancesAction
  | SetFarmEarnedAction
  | SetFarmTotalSupplyAction;

const reducer = (state: FarmContextState, action: Action): FarmContextState => {
  switch (action.type) {
    case 'SET_FARMS':
      return { ...state, farmPairs: action.payload };
    case 'SET_BALANCES':
      return { ...state, farmBalances: action.payload };
    case 'SET_EARNED':
      return { ...state, farmEarned: action.payload };
    case 'SET_TOTAL_SUPPLY':
      return { ...state, farmTotalSupply: action.payload };
    default:
      return state;
  }
};

/**
 * Hook to handle all farms data fetching and processing
 */
export const useFarmsData = () => {
  const { provider } = useTrackedState();
  const { account } = useAppWallet();
  const { liquidityPairs } = useLiquidityPairs();
  const dexRates = useDexRates();
  const exchangeRates = useExchangeRates();

  const providerData = findProvider(provider);

  // Memoize rates to prevent excessive re-renders
  // Only recalculate when the actual values change, not when object reference changes
  const dexRatesStringified = useMemo(
    () => JSON.stringify(dexRates),
    [dexRates],
  );
  const exchangeRatesStringified = useMemo(
    () => JSON.stringify(exchangeRates),
    [exchangeRates],
  );
  const dexRatesMemoized = useMemo(() => dexRates, [dexRatesStringified]);
  const exchangeRatesMemoized = useMemo(
    () => exchangeRates,
    [exchangeRatesStringified],
  );

  const [{ farmPairs, farmBalances, farmEarned, farmTotalSupply }, dispatch] =
    useReducer(reducer, DEFAULT_STATE);

  const rawList = useMemo(() => getFarmListForProvider(provider), [provider]);

  const callsResponse = useCalls(
    [
      ...rawList.map(farm => {
        return {
          contract: getStakingContract(
            farm.contractAddress,
            farm?.dynamicRewards,
          ),
          method: farm?.dynamicRewards ? 'rewards' : 'rewardRate',
          args: farm?.rewardTokens?.map(item => item.token) ?? [],
        };
      }),
      ...rawList.map(farm => {
        return {
          contract: getStakingContract(
            farm.contractAddress,
            farm?.dynamicRewards,
          ),
          method: 'totalSupply',
          args: [],
        };
      }),
      ...rawList.map(farm => {
        return {
          contract: getStakingContract(
            farm.contractAddress,
            farm?.dynamicRewards,
          ),
          method: 'balanceOf',
          args: [TEST_ACCOUNT || account || ZERO_ADDRESS],
        };
      }),
      ...rawList.map(farm => {
        return {
          contract: getStakingContract(
            farm.contractAddress,
            farm?.dynamicRewards,
          ),
          method: 'earned',
          args: [TEST_ACCOUNT || account || ZERO_ADDRESS],
        };
      }),
      ...rawList.map(farm => {
        return {
          contract: getStakingContract(
            farm.contractAddress,
            farm?.dynamicRewards,
          ),
          method: 'periodFinished',
          args: [],
        };
      }),
    ],
    { chainId: providerData.supportedChains?.[0]?.chainId ?? 1 },
  );

  useEffect(() => {
    if (callsResponse.length === 0) {
      return;
    }

    const [rewardRates, totalSupplies, balances, earned, periodsFinished] =
      chunkArray(callsResponse, rawList.length);

    const appendedApyList = rawList.map((farm, index) => {
      const pair = liquidityPairs?.find(
        p => p.id.toLowerCase() === farm.pair.id.toLowerCase(),
      );

      const tokenSupply = parseFloat(
        utils.formatUnits(
          totalSupplies[index]?.value?.[0] ?? DEFAULT_BIGNUM,
          DEFAULT_DECIMALS,
        ),
      );
      // Use reward token decimals if available, fallback to DEFAULT_DECIMALS
      const rewardTokenDecimals =
        farm.rewardToken?.decimals ?? DEFAULT_DECIMALS;
      const rewardRate = parseFloat(
        utils.formatUnits(
          rewardRates[index]?.value?.[0] ?? DEFAULT_BIGNUM,
          rewardTokenDecimals,
        ),
      );

      const periodFinishedBn = periodsFinished[index]?.value?.[0];
      if (periodFinishedBn === undefined) {
        return { ...farm, apy: '...' };
      }

      if (isFarmRewardsPeriodEnded(periodFinishedBn)) {
        return { ...farm, apy: '0.00' };
      }

      if (farm.singleSided) {
        const periodFinished = parseFloat(
          utils.formatUnits(periodFinishedBn, 0),
        );

        const stakingApy = getStakingAPY(
          farm,
          rewardRate,
          0,
          tokenSupply,
          dexRatesMemoized,
          provider,
          exchangeRatesMemoized,
          periodFinished,
        );
        return { ...farm, apy: stakingApy };
      }

      if (
        !pair ||
        !totalSupplies?.[index]?.value ||
        !dexRatesMemoized?.[provider]?.[farm.rewardToken.abbr.toUpperCase()]
      ) {
        return { ...farm, apy: '...' };
      }

      // Use the EXACT same calculation as the table display (InvestmentRow.tsx line 159)
      // This ensures APY uses the same totalSupplyFiat value shown in the table
      const tokenRate = farm.singleSided
        ? dexRates?.[provider]?.[pair.token0?.symbol?.toUpperCase()] || 0
        : 0;

      // Calculate totalSupplyFiat using the same method as the table
      // The table uses: lpTokenValue(totalSupply, pair, tokenRate)
      // where totalSupply comes from farmTotalSupply[farm.contractAddress]
      // We use tokenSupply which is the same value, just parsed directly from the contract call
      const totalSupplyFiat = lpTokenValue(tokenSupply, pair, tokenRate);

      // Debug: Log pair data to ensure it matches what the table uses
      if (!pair.reserveUSD || !pair.totalSupply || totalSupplyFiat === 0) {
        // console.warn('[useFarmsData] Pair data check for APY calculation:', {
        //   farm: farm.contractAddress,
        //   pairId: pair.id,
        //   pairName: pair.name,
        //   reserveUSD: pair.reserveUSD,
        //   totalSupply: pair.totalSupply,
        //   tokenSupply,
        //   tokenRate,
        //   totalSupplyFiat,
        //   calculatedPrice: pair.totalSupply
        //     ? parseFloat(pair.reserveUSD || '0') /
        //       parseFloat(pair.totalSupply || '1')
        //     : 'N/A',
        // });
      }

      const periodFinished = parseFloat(
        utils.formatUnits(
          periodsFinished[index]?.value?.[0] ?? DEFAULT_BIGNUM,
          0,
        ),
      );

      // Check if period is actually finished
      // Only treat as finished if periodFinished is a valid timestamp and in the past
      // AND rewardRate is 0 (no rewards being distributed)
      // Some contracts may have old periodFinished values but still be active
      const isPeriodFinished =
        periodFinished &&
        periodFinished > 0 &&
        Date.now() > periodFinished * 1000 &&
        rewardRate <= 0;

      if (isPeriodFinished) {
        return { ...farm, apy: '0.00' };
      }

      const totalYearlyRewards = rewardRate * SECONDS_IN_YEAR;

      // Use WebSocket exchange rates first (more accurate), fallback to dexRates
      // Validate rates are reasonable (between $0.00001 and $1,000,000)
      const MIN_VALID_RATE = 0.00001;
      const MAX_VALID_RATE = 1000000;

      const isValidRate = (rate: number | undefined): boolean => {
        return (
          rate !== undefined &&
          Number.isFinite(rate) &&
          rate > 0 &&
          rate >= MIN_VALID_RATE &&
          rate <= MAX_VALID_RATE
        );
      };

      const rewardTokenExchangeRate =
        exchangeRatesMemoized?.[farm.rewardToken.abbr?.toUpperCase()]?.USD;
      const rewardTokenDexRate =
        dexRatesMemoized?.[provider]?.[farm.rewardToken.abbr?.toUpperCase()] ||
        0;

      // Fallback: Calculate VERSE price from pool's derivedETH (same source as TVL calculation)
      // This uses the same pool data that's used for TVL, ensuring consistency
      let rewardTokenFiatRateFromPool = 0;
      if (pair && farm.rewardToken.abbr) {
        // Find which token in the pair is the reward token
        let rewardTokenInPair = null;
        if (
          pair.token0?.symbol?.toUpperCase() ===
          farm.rewardToken.abbr.toUpperCase()
        ) {
          rewardTokenInPair = pair.token0;
        } else if (
          pair.token1?.symbol?.toUpperCase() ===
          farm.rewardToken.abbr.toUpperCase()
        ) {
          rewardTokenInPair = pair.token1;
        }

        if (rewardTokenInPair?.derivedETH) {
          // Get ETH price from exchange rates
          const ethPrice =
            exchangeRatesMemoized?.ETH?.USD ||
            exchangeRatesMemoized?.WETH?.USD ||
            0;
          if (ethPrice > 0) {
            // Calculate token price: derivedETH * ETH_price (same as DexRatesProvider)
            const derivedETHValue = parseFloat(rewardTokenInPair.derivedETH);
            rewardTokenFiatRateFromPool = derivedETHValue * ethPrice;
          }
        }
      }

      let rewardTokenFiatRate = 0;

      // For VERSE token, use the EXACT same price calculation as TVL
      // TVL uses: exchangeRates.VERSE.USD (WebSocket rate)
      // This ensures APY calculation uses the same price shown in TVL
      if (farm.rewardToken.abbr?.toUpperCase() === 'VERSE') {
        // Priority 1: WebSocket exchange rates (same as TVL uses in VerseStaking.tsx line 52)
        if (isValidRate(rewardTokenExchangeRate)) {
          rewardTokenFiatRate = rewardTokenExchangeRate;
        }
        // Priority 2: Calculate from pool's derivedETH (fallback if WebSocket not available)
        else if (isValidRate(rewardTokenFiatRateFromPool)) {
          rewardTokenFiatRate = rewardTokenFiatRateFromPool;
        }
        // NEVER use dexRate for VERSE - it's often inflated (302 million)
        // If both WebSocket and pool-calculated rates fail, use 0 (will show APY as "...")
      } else if (isValidRate(rewardTokenExchangeRate)) {
        // For other tokens, use standard priority order
        // Priority 1: WebSocket exchange rates (most accurate)
        rewardTokenFiatRate = rewardTokenExchangeRate;
      } else if (isValidRate(rewardTokenFiatRateFromPool)) {
        // Priority 2: Calculate from pool's derivedETH
        rewardTokenFiatRate = rewardTokenFiatRateFromPool;
      } else if (
        isValidRate(rewardTokenDexRate) &&
        rewardTokenDexRate <= MAX_VALID_RATE
      ) {
        // Priority 3: dexRates (but validate it's not inflated)
        rewardTokenFiatRate = rewardTokenDexRate;
      }

      // Final safety check: if rate is still invalid or exceeds max, don't use it
      // This is a critical check to prevent inflated APY calculations
      if (
        !isValidRate(rewardTokenFiatRate) ||
        rewardTokenFiatRate > MAX_VALID_RATE
      ) {
        // console.warn(
        //   '[useFarmsData] Invalid rewardTokenFiatRate detected, setting to 0:',
        //   {
        //     farm: farm.contractAddress,
        //     rewardToken: farm.rewardToken.abbr,
        //     rewardTokenFiatRate,
        //     exceedsMax: rewardTokenFiatRate > MAX_VALID_RATE,
        //     maxValidRate: MAX_VALID_RATE,
        //     exchangeRate: rewardTokenExchangeRate,
        //     exchangeRateValid: isValidRate(rewardTokenExchangeRate),
        //     poolCalculatedRate: rewardTokenFiatRateFromPool,
        //     poolCalculatedRateValid: isValidRate(rewardTokenFiatRateFromPool),
        //     dexRate: rewardTokenDexRate,
        //     dexRateExceedsMax: rewardTokenDexRate > MAX_VALID_RATE,
        //     dexRateValid: isValidRate(rewardTokenDexRate),
        //   },
        // );
        rewardTokenFiatRate = 0;
      }

      // Warn if using wrong dexRate or if WebSocket rate is missing
      // Always log rate selection to debug APY inflation issues
      if (
        rewardTokenFiatRate === rewardTokenDexRate ||
        !rewardTokenExchangeRate ||
        rewardTokenFiatRate > MAX_VALID_RATE
      ) {
        // console.warn('[useFarmsData] Rate selection for APY calculation:', {
        //   farm: farm.contractAddress,
        //   rewardToken: farm.rewardToken.abbr,
        //   exchangeRate: rewardTokenExchangeRate,
        //   exchangeRateValid: isValidRate(rewardTokenExchangeRate),
        //   poolCalculatedRate: rewardTokenFiatRateFromPool,
        //   poolCalculatedRateValid: isValidRate(rewardTokenFiatRateFromPool),
        //   dexRate: rewardTokenDexRate,
        //   dexRateValid: isValidRate(rewardTokenDexRate),
        //   used: rewardTokenFiatRate,
        //   source: (() => {
        //     if (rewardTokenFiatRate === rewardTokenExchangeRate) {
        //       return 'WebSocket';
        //     }
        //     if (rewardTokenFiatRate === rewardTokenFiatRateFromPool) {
        //       return 'Pool (derivedETH)';
        //     }
        //     if (rewardTokenFiatRate === rewardTokenDexRate) {
        //       return 'dexRates';
        //     }
        //     return 'NONE (using 0)';
        //   })(),
        //   issue: (() => {
        //     if (rewardTokenFiatRate > MAX_VALID_RATE) {
        //       return 'INVALID RATE - exceeds max';
        //     }
        //     if (!rewardTokenExchangeRate) {
        //       return 'WebSocket rate missing';
        //     }
        //     return 'Using fallback rate';
        //   })(),
        // });
      }

      const yearlyRewardsFiat = totalYearlyRewards * rewardTokenFiatRate;

      // Guard against invalid calculations
      if (!totalSupplyFiat || totalSupplyFiat <= 0) {
        // console.warn(
        //   '[useFarmsData] totalSupplyFiat is 0 or invalid - returning "..."',
        //   {
        //     farm: farm.contractAddress,
        //     tokenSupply,
        //     pairReserveUSD: pair.reserveUSD,
        //     pairTotalSupply: pair.totalSupply,
        //     totalSupplyFiat,
        //   },
        // );
        return { ...farm, apy: '...' };
      }

      if (
        !Number.isFinite(yearlyRewardsFiat) ||
        !Number.isFinite(totalSupplyFiat)
      ) {
        // console.warn(
        //   '[useFarmsData] Invalid values detected - returning "..."',
        //   {
        //     farm: farm.contractAddress,
        //     yearlyRewardsFiat,
        //     totalSupplyFiat,
        //     totalYearlyRewards,
        //     rewardTokenFiatRate,
        //   },
        // );
        return { ...farm, apy: '...' };
      }

      const estimatedAPY = (yearlyRewardsFiat / totalSupplyFiat) * 100;

      // Only log LP farm calculations when there's an issue (high APY or invalid rate)
      // This reduces console spam from excessive re-renders
      if (estimatedAPY > 100 || rewardTokenFiatRate > MAX_VALID_RATE) {
        // console.warn(
        //   '[useFarmsData] LP Farm APY calculation (ISSUE DETECTED):',
        //   {
        //     farm: farm.contractAddress,
        //     farmName: `${farm.stakingToken?.abbr || 'N/A'}/${
        //       farm.rewardToken?.abbr || 'N/A'
        //     }`,
        //     tokenSupply,
        //     pairReserveUSD: pair.reserveUSD,
        //     pairTotalSupply: pair.totalSupply,
        //     totalSupplyFiat,
        //     rewardRate,
        //     totalYearlyRewards,
        //     rewardTokenFiatRate,
        //     yearlyRewardsFiat,
        //     estimatedAPY,
        //     periodFinished,
        //     isPeriodActive: Date.now() <= periodFinished * 1000,
        //   },
        // );
      }

      // Guard against NaN or Infinity
      if (!Number.isFinite(estimatedAPY)) {
        // console.warn(
        //   '[useFarmsData] estimatedAPY is NaN or Infinity - returning "..."',
        //   {
        //     farm: farm.contractAddress,
        //     yearlyRewardsFiat,
        //     totalSupplyFiat,
        //     estimatedAPY,
        //   },
        // );
        return { ...farm, apy: '...' };
      }

      // Debug logging to identify inflated APY issues
      if (estimatedAPY > 100 || !totalSupplyFiat || tokenSupply < 0.01) {
        // console.warn('[useFarmsData] High APY detected - checking values:', {
        //   farm: farm.contractAddress,
        //   farmName: `${farm.stakingToken?.abbr || 'N/A'}/${
        //     farm.rewardToken?.abbr || 'N/A'
        //   }`,
        //   rewardToken: farm.rewardToken.abbr,
        //   rewardTokenDecimals: farm.rewardToken?.decimals ?? DEFAULT_DECIMALS,
        //   rewardRateRaw,
        //   rewardRate,
        //   rewardRatePerSecond: rewardRate,
        //   rewardRatePerYear: totalYearlyRewards,
        //   totalYearlyRewards,
        //   rewardTokenFiatRate,
        //   yearlyRewardsFiat,
        //   tokenSupply,
        //   pairReserveUSD: pair.reserveUSD,
        //   pairTotalSupply: pair.totalSupply,
        //   totalSupplyFiat,
        //   estimatedAPY,
        //   periodFinished,
        //   isPeriodActive: Date.now() <= periodFinished * 1000,
        //   lpTokenPrice: pair.totalSupply
        //     ? parseFloat(pair.reserveUSD) / parseFloat(pair.totalSupply)
        //     : 'N/A',
        //   calculation: {
        //     numerator: yearlyRewardsFiat,
        //     denominator: totalSupplyFiat,
        //     result: estimatedAPY,
        //   },
        // });
      }

      // If totalSupplyFiat is too small, APY calculation is unreliable
      // Minimum threshold: $1 USD to avoid inflated APY from rounding errors
      const MIN_TOTAL_SUPPLY_FIAT = 1;
      if (
        !tokenSupply ||
        !totalSupplyFiat ||
        totalSupplyFiat < MIN_TOTAL_SUPPLY_FIAT
      ) {
        return { ...farm, apy: '...' };
      }

      // If APY is 0 or negative (period finished or no rewards), return '0.00'
      if (estimatedAPY <= 0) {
        return { ...farm, apy: '0.00' };
      }

      if (estimatedAPY > 500) return { ...farm, apy: '> 500' };

      return {
        ...farm,
        apy: estimatedAPY.toFixed(2),
      };
    });

    const sortedFarms = sortFarmsList(appendedApyList);

    const reducedBalances = rawList.reduce(
      (prev, curr, index) => ({
        ...prev,
        [curr.contractAddress]: balances[index]?.value?.[0] as BigNumber,
      }),
      {} as Record<string, BigNumber>,
    );

    dispatch({ type: 'SET_BALANCES', payload: reducedBalances });

    const reducedEarned = rawList.reduce((prev, curr, index) => {
      const earnedResp = earned[index]?.value?.[0];
      const earnedFlat = curr?.dynamicRewards
        ? (earnedResp as BigNumber[])?.reduce(
            (acc, cur) => acc.add(BigNumber.from(cur)),
            DEFAULT_BIGNUM,
          )
        : earnedResp || DEFAULT_BIGNUM;
      return {
        ...prev,
        [curr.contractAddress]: earnedFlat,
      };
    }, {} as Record<string, BigNumber>);

    dispatch({ type: 'SET_EARNED', payload: reducedEarned });

    const reducedTotalSupply = rawList.reduce(
      (prev, curr, index) => ({
        ...prev,
        [curr.contractAddress]: totalSupplies[index]?.value?.[0] as BigNumber,
      }),
      {} as Record<string, BigNumber>,
    );

    dispatch({ type: 'SET_TOTAL_SUPPLY', payload: reducedTotalSupply });

    const activeFarms = sortedFarms.filter(
      farm => !(reducedBalances[farm.contractAddress] && farm.hidden),
    );

    dispatch({ type: 'SET_FARMS', payload: activeFarms });
  }, [
    callsResponse,
    rawList,
    liquidityPairs,
    dexRatesMemoized,
    exchangeRatesMemoized,
    provider,
  ]);

  return {
    farmPairs,
    farmBalances,
    farmEarned,
    farmTotalSupply,
  };
};
